import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-white py-20">
      <SEOHead page="cookiePolicy" />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Cookie Policy</h1>
            <p className="text-lg text-slate-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="prose prose-slate max-w-none space-y-8">
            <div>
              <p className="text-slate-600">
                This Cookie Policy explains how Digian.ai ("we," "us," or "our") uses cookies and similar tracking technologies when you visit our website or use our services. By continuing to use our site, you consent to our use of cookies as described in this policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-slate-600">
                Cookies are small text files placed on your device by a website when you visit it. They are widely used to make websites work more efficiently, provide a better user experience, and give site owners information about how the site is being used. Cookies may be "session" cookies (deleted when you close your browser) or "persistent" cookies (stored on your device until they expire or you delete them).
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Types of Cookies We Use</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Essential Cookies</h3>
                  <p className="text-slate-600">These cookies are strictly necessary for the website to function. They enable core features such as security, authentication, and session management. You cannot opt out of these cookies.</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                    <li>Authentication tokens (JWT) to keep you logged in</li>
                    <li>Session identifiers for secure access</li>
                    <li>CSRF protection tokens</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Performance & Analytics Cookies</h3>
                  <p className="text-slate-600">These cookies help us understand how visitors interact with our website by collecting information anonymously. We use this data to improve site performance and user experience.</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                    <li>Page view counts and navigation paths</li>
                    <li>Time spent on pages and bounce rates</li>
                    <li>Error and performance monitoring</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Functional Cookies</h3>
                  <p className="text-slate-600">These cookies allow the website to remember choices you make (such as your language or region) and provide enhanced, personalised features.</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                    <li>User interface preferences</li>
                    <li>Region and language settings</li>
                    <li>Previously entered form data</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Marketing & Targeting Cookies</h3>
                  <p className="text-slate-600">These cookies track your browsing activity to display relevant advertisements and measure the effectiveness of our marketing campaigns. They may be set by us or by third-party advertising partners.</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                    <li>Ad personalisation and retargeting</li>
                    <li>Campaign conversion tracking</li>
                    <li>Social media sharing and engagement</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Third-Party Cookies</h2>
              <p className="text-slate-600 mb-4">
                We may allow selected third-party services to place cookies on your device to support analytics, advertising, and social media integrations. These third parties have their own privacy and cookie policies, which we encourage you to review.
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Google Analytics</strong> — website usage analytics</li>
                <li><strong>Microsoft Clarity / Azure</strong> — session recording and heatmaps</li>
                <li><strong>LinkedIn Insight Tag</strong> — B2B campaign analytics</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. How to Manage Cookies</h2>
              <p className="text-slate-600 mb-4">
                You can control and manage cookies in several ways:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Browser settings:</strong> Most browsers allow you to block or delete cookies via their settings. Refer to your browser's help documentation for instructions.</li>
                <li><strong>Opt-out tools:</strong> You can opt out of Google Analytics at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">tools.google.com/dlpage/gaoptout</a>.</li>
                <li><strong>Do Not Track:</strong> You can enable the "Do Not Track" setting in your browser, though not all websites honour this signal.</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Please note that disabling certain cookies may affect the functionality and performance of our website.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Changes to This Cookie Policy</h2>
              <p className="text-slate-600">
                We may update this Cookie Policy periodically to reflect changes in technology, legislation, or our business practices. The "Last updated" date at the top of this page will be revised accordingly. We encourage you to review this policy regularly.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Contact Us</h2>
              <p className="text-slate-600 mb-4">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                <p className="text-slate-600"><strong>Email:</strong> <a href="mailto:contact@digian.ai" className="text-indigo-600 hover:text-indigo-800">contact@digian.ai</a></p>
                <p className="text-slate-600"><strong>Website:</strong> <a href="https://digian.ai" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">digian.ai</a></p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
